import { useState, useEffect, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { changeDetails } from '../../../api-client/identity'
import { APIErrors } from '../../../api-client/enums'
import useValidation from '../../../hooks/use-validation'
import { useAuth } from '../../../contexts/auth'
import { Prefilled } from '../../../components/inputs'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import { Button } from '../../../components/button'
import isZipCodeValid from '../../../utils/is-zip-code-valid'
import isPhoneNumbersValid from '../../../utils/is-phone-number-valid'

const errorMessages = {
  phoneNumberNotValid: "Phone Number doesn't look valid",
  zipCodeNotValid: 'Please enter a valid postal code',
  countryNotSupported: 'Country is not supported by privauto',
}

export default function AccountInformation({ onEmailEdit, onClose, onSuccess }) {
  const [{ userDetails }, { authResync }] = useAuth()
  const [state, setState] = useState({
    firstName: userDetails.firstName,
    lastName: userDetails.lastName,
    email: userDetails.email,
    phoneNumber: formatPhoneNumber(userDetails.phoneNumber),
    zipCode: userDetails.zipCode,
  })
  const [mappedServerErrors, setMappedServerErrors] = useState({})
  const [isFormBeingEdited, setIsFormBeingEdited] = useState(false)

  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)

  const { mutate, isLoading, error } = useMutation({
    mutationFn: changeDetails,
    onSuccess,
    onSettled: () => {
      authResync()
    },
  })

  useEffect(() => {
    setMappedServerErrors(mapServerErrors(error?.errors))
  }, [error])

  const handleInputChange = (e) => {
    e.persist()
    setIsFormBeingEdited(true)

    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const updateDetails = useCallback(() => {
    if (!isFormValid) {
      showErrors()
    } else {
      mutate({
        firstName: state.firstName,
        lastName: state.lastName,
        zipCode: state.zipCode,
        phoneNumber: state.phoneNumber.replace(/[^\d]/g, ''),
      })
    }

    setIsFormBeingEdited(false)
  }, [mutate, isFormValid, showErrors, state])

  const handleButtonClick = () => (isFormBeingEdited ? updateDetails() : onClose())

  return (
    <div className='wrapper'>
      <h1 className='title'>Account Information</h1>
      <p className='description'>Update your account details</p>
      <div className='inputFieldWrapper firstName'>
        <Prefilled
          type='text'
          name='firstName'
          error={errors.firstName}
          value={state.firstName}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper lastName'>
        <Prefilled
          type='text'
          name='lastName'
          error={errors.lastName}
          value={state.lastName}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper email'>
        <Prefilled
          type='email'
          name='email'
          error={errors.email}
          value={state.email}
          onEdit={onEmailEdit}
        />
      </div>

      <div className='inputFieldWrapper phoneNumber'>
        {(errors.phoneNumber || mappedServerErrors.phoneNumber) && (
          <span className='error'>{errors.phoneNumber || mappedServerErrors.phoneNumber}</span>
        )}
        <Prefilled
          type='text'
          name='phoneNumber'
          inputMode='numeric'
          maxLength='12'
          error={errors.phoneNumber || mappedServerErrors.phoneNumber}
          value={formatPhoneNumber(state.phoneNumber)}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper zipCode'>
        {(errors.zipCode || mappedServerErrors.zipCode) && (
          <span className='error'>{errors.zipCode || mappedServerErrors.zipCode}</span>
        )}
        <Prefilled
          type='text'
          name='zipCode'
          inputMode='numeric'
          error={errors.zipCode || mappedServerErrors.zipCode}
          value={state.zipCode}
          onChange={handleInputChange}
        />
      </div>

      <Button className='saveBtn' onClick={handleButtonClick} disabled={isLoading}>
        {isFormBeingEdited ? 'Save' : 'Close'}
      </Button>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.8rem 4.1rem;
          background: #fff;
          border-radius: 1rem;

          :global(.saveBtn) {
            display: block;
            margin: 0 auto;
          }
        }

        .inputFieldWrapper {
          margin: 0 0 1.7rem;

          :global(.inputContainer > input) {
            background: #fff !important;
          }

          :global(.inputContainer > .edit) {
            background: transparent !important;
          }
        }

        .title {
          margin: 0 0 0.8rem;
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          max-width: 32rem;
          margin: 0 auto 2.3rem;
          text-align: center;
          font-size: 1.6rem;
        }

        .inputFieldWrapper {
          position: relative;
        }

        .error {
          display: block;
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;
        }

        .email {
          :global(input.email) {
            color: var(--secondary-text-color) !important;
          }

          :global(.adornment) {
            button {
              background: transparent;
              color: var(--primary-color);
              font-size: 1.6rem;
            }
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 2.7rem 1.5rem 2.3rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .title {
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .description {
            margin: 0.2rem auto 3rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }
        }
      `}</style>
    </div>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  if (state.firstName.length < 1) errors['firstName'] = true
  if (state.lastName.length < 1) errors['lastName'] = true
  if (state.phoneNumber.length < 1 || !isPhoneNumbersValid(state.phoneNumber))
    errors['phoneNumber'] = errorMessages.phoneNumberNotValid
  if (state.zipCode.length < 1 || !isZipCodeValid(state.zipCode))
    errors['zipCode'] = errorMessages.zipCodeNotValid

  if (Object.entries(errors).length > 0) isValid = false

  return { isValid, errors }
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (+error?.code) {
      case APIErrors.InvalidPhoneNumberFormat:
        mappedErrors['phoneNumber'] = errorMessages.phoneNumberNotValid
        break
      case APIErrors.InvalidZipCode:
      case APIErrors.InvalidGoogleApiResponseStatusCode:
      case APIErrors.TimezoneNotFound:
      case APIErrors.TimezoneNotSupported:
      case APIErrors.ZipCodeDoesNotExists:
        mappedErrors['zipCode'] = errorMessages.zipCodeNotValid
        break
      case APIErrors.CountryNotSupported:
        mappedErrors['password'] = errorMessages.countryNotSupported
        break
      default:
        mappedErrors[APIErrors.Unknown] = error.message
        break
    }
  })

  return mappedErrors
}
